import axios from 'axios';
import config from '@/data/config';
import { decryptToken, extractTokenFromPersistedBlob } from '@/utils/tokenStorage';
import { dispatchWindowEvent } from '@/utils/windowEvent';
import { checkDeployVersionHeader } from '@/utils/deployVersionWatcher';

/** globals */

const VUEX_STORAGE_KEY = 'vuex';
const REFRESH_TOKEN_ENDPOINT = '/users/refresh-token';
// two separate keys: PROACTIVE_GATE is a coarse "checked recently?" clock (safe
// to seed at boot); REFRESH_STATE is a short-lived cross-tab "renewing right
// now" signal — merging them would make a boot-time seed look like an in-flight renewal
const PROACTIVE_GATE_KEY = 'token_proactive_gate';
const REFRESH_STATE_KEY = 'token_refresh_state';

const REFRESH_DEBOUNCE_DELAY = 500;
const REFRESH_TOKEN_MAX_CONSECUTIVE_FAILURES = 3;
// old failures (e.g. from a JWT blacklist race hours ago) must not keep counting
// against a tab forever — only a burst within this window trips the breaker
const REFRESH_FAILURE_RESET_WINDOW = 10 * 60 * 1000;
const PROACTIVE_REFRESH_INTERVAL = 5 * 60 * 1000;
// must comfortably outlast a slow/unstable backend, not just network latency
const REFRESH_IN_FLIGHT_WINDOW = 8 * 1000;
// must be >= REFRESH_IN_FLIGHT_WINDOW, otherwise a waiting tab always gives up
// right before the window it was told to trust elapses
const CROSS_TAB_WAIT_TIMEOUT = 9 * 1000;
// before reloading on a cross-tab logout signal, give the tab a moment to see
// whether a fresher token lands right after (a blacklist-race false alarm)
const CROSS_TAB_LOGOUT_DEBOUNCE = 2 * 1000;

let consecutiveRefreshFailures = 0;
let lastRefreshFailureAt = 0;
let tokenRenewalPromise = null;
let ownLastAttemptAt = 0;
let lastKnownToken = null;

/** http creation */

const httpV1 = axios.create({
  baseURL: `${config.BASE_API_URL}/v1`,
  headers: {
    'content-type': 'application/json',
  },
});
const httpV2 = axios.create({
  baseURL: `${config.BASE_API_URL}/v2`,
  headers: {
    'content-type': 'application/json',
  },
});

/** methods */

const getToken = () => {
  try {
    let store = localStorage.getItem(VUEX_STORAGE_KEY);

    if (!store) throw new Error('Store not found');
    store = JSON.parse(store);

    if (!store.app.token) throw new Error('Token not found');
    return decryptToken(store.app.token);
  } catch (err) {
    return err;
  }
};

/** cross-tab coordination */

const readSharedTimestamp = (key) => {
  try {
    return JSON.parse(localStorage.getItem(key))?.at || 0;
  } catch {
    return 0;
  }
};

const writeSharedTimestamp = (key, timestamp) => {
  try {
    localStorage.setItem(key, JSON.stringify({ at: timestamp }));
  } catch {
    /* noop */
  }
};

// resolves once another tab's renewal lands a new token, so this tab avoids
// firing a competing refresh-token call of its own
const waitForCrossTabToken = (timeoutMs) => new Promise((resolve, reject) => {
  const current = getToken();
  const previousToken = typeof current === 'string' ? current : null;
  let timer;

  const onStorage = (event) => {
    if (event.key !== VUEX_STORAGE_KEY || !event.newValue) return;

    const token = extractTokenFromPersistedBlob(event.newValue);
    if (!token || token === previousToken) return;

    clearTimeout(timer);
    window.removeEventListener('storage', onStorage);
    resolve(token);
  };

  timer = setTimeout(() => {
    window.removeEventListener('storage', onStorage);
    reject(new Error('Cross-tab refresh wait timed out'));
  }, timeoutMs);

  window.addEventListener('storage', onStorage);
});

const requestNewToken = () => {
  const attemptTimestamp = Date.now();
  ownLastAttemptAt = attemptTimestamp;
  writeSharedTimestamp(REFRESH_STATE_KEY, attemptTimestamp);
  writeSharedTimestamp(PROACTIVE_GATE_KEY, attemptTimestamp);

  tokenRenewalPromise = new Promise((resolve, reject) => {
    setTimeout(async () => { // NOSONAR: callback assíncrono intencional
      try {
        const response = await httpV1.post(REFRESH_TOKEN_ENDPOINT);

        consecutiveRefreshFailures = 0;
        return resolve(response.data.data);
      } catch (err) {
        consecutiveRefreshFailures += 1;
        lastRefreshFailureAt = Date.now();
        return reject(err);
      } finally {
        tokenRenewalPromise = null;
      }
    }, REFRESH_DEBOUNCE_DELAY);
  });

  return tokenRenewalPromise;
};

const renewToken = async () => {
  // a burst of failures long ago (e.g. a JWT blacklist race) shouldn't keep
  // arming the breaker against attempts that have nothing to do with it
  if (Date.now() - lastRefreshFailureAt > REFRESH_FAILURE_RESET_WINDOW) {
    consecutiveRefreshFailures = 0;
  }

  if (consecutiveRefreshFailures >= REFRESH_TOKEN_MAX_CONSECUTIVE_FAILURES) {
    return Promise.reject(new Error('Max attempts reached'));
  }

  if (tokenRenewalPromise) return tokenRenewalPromise; // same-tab dedup

  const sharedLastAttempt = readSharedTimestamp(REFRESH_STATE_KEY);
  const anotherTabIsLikelyRenewing = sharedLastAttempt !== ownLastAttemptAt
    && Date.now() - sharedLastAttempt < REFRESH_IN_FLIGHT_WINDOW;

  if (anotherTabIsLikelyRenewing) {
    try {
      const token = await waitForCrossTabToken(CROSS_TAB_WAIT_TIMEOUT);
      return { token };
    } catch {
      return requestNewToken(); // nobody delivered a token in time — try it ourselves
    }
  }

  return requestNewToken();
};

const dispatchRefreshTokenEvent = (token) => {
  lastKnownToken = token;
  dispatchWindowEvent('refreshToken', token);
};

const isRefreshTokenRequest = (requestConfig) => requestConfig?.url === REFRESH_TOKEN_ENDPOINT;

const maybeTriggerProactiveRefresh = (requestConfig) => {
  // don't recurse off the refresh call's own request
  if (isRefreshTokenRequest(requestConfig)) return;

  // getToken() returns an Error, not null, when unauthenticated
  const token = getToken();
  if (!token || token instanceof Error) return;

  if (Date.now() - readSharedTimestamp(PROACTIVE_GATE_KEY) < PROACTIVE_REFRESH_INTERVAL) return;

  renewToken()
    .then(({ token: newToken }) => dispatchRefreshTokenEvent(newToken))
    .catch((err) => {
      // never forces logout here — only a reactive 401 means the token is actually invalid
      console.log('%c proactive-refresh-failed ', 'background:orange;color:black;', err);
    });
};

const handleCrossTabSync = (event) => {
  if (event.key !== VUEX_STORAGE_KEY || !event.newValue) return;

  try {
    const parsed = JSON.parse(event.newValue);

    if (parsed?.app?.token === false) {
      // only a real authenticated->false TRANSITION means "logged out elsewhere".
      // vuex-persist also writes token:false as the normal resting state for a tab
      // that was never logged in — reacting to that would bounce two unauthenticated
      // tabs (e.g. two login-page tabs) into reloading each other forever.
      const wasAuthenticated = typeof lastKnownToken === 'string' && lastKnownToken;
      lastKnownToken = false;

      // don't reload immediately: a JWT blacklist race can make this tab observe
      // a transient token:false right before another tab lands the real renewed
      // token — wait a beat and only bounce if this tab is still unauthenticated
      if (wasAuthenticated) {
        setTimeout(() => {
          const currentToken = getToken();
          if (!currentToken || currentToken instanceof Error) window.location.replace('/');
        }, CROSS_TAB_LOGOUT_DEBOUNCE);
      }
      return;
    }

    const token = extractTokenFromPersistedBlob(event.newValue);
    if (token && token !== lastKnownToken) dispatchRefreshTokenEvent(token);
  } catch {
    /* noop */
  }
};

/** interceptors :: methods */

const handleRequest = (config) => {
  try {
    const token = getToken();

    if (token) config.headers.Authorization = `Bearer ${token}`;

    maybeTriggerProactiveRefresh(config);

    return config;
  } catch (err) {
    return Promise.reject(err);
  }
};

const handleResponseSuccess = (response) => {
  checkDeployVersionHeader(response?.headers);

  return response;
};

const handleResponseError = async (err) => {
  // checked on error responses too — a 4xx/5xx still carries the header
  checkDeployVersionHeader(err?.response?.headers);

  if (err?.response?.status !== 401) return Promise.reject(err);

  try {
    const { token } = await renewToken();

    dispatchRefreshTokenEvent(token);

    err.config.headers.Authorization = `Bearer ${token}`;

    return axios.request(err.config);
  } catch (err) {
    console.log(
      '%c handle-response-error ',
      'background:red;color:white;',
      err,
    );

    window.location.replace('/');

    return Promise.reject(err);
  }
};

/** instances */

httpV1.interceptors.request.use(handleRequest);
httpV2.interceptors.request.use(handleRequest);

httpV1.interceptors.response.use(handleResponseSuccess, handleResponseError);
httpV2.interceptors.response.use(handleResponseSuccess, handleResponseError);

window.addEventListener('storage', handleCrossTabSync);

const initialToken = getToken();
if (typeof initialToken === 'string') lastKnownToken = initialToken;

// avoids treating a brand new session as "interval already elapsed", which
// would fire a redundant proactive refresh on the very first activity after login
if (!readSharedTimestamp(PROACTIVE_GATE_KEY)) {
  writeSharedTimestamp(PROACTIVE_GATE_KEY, Date.now());
}

export {
  httpV1,
  httpV2,
};
